import { Select } from '@/components/common';
import { SelectOptions } from '@/model/CommonModel';
import { ReaderError } from '@/model/DeviceAdminModel';
import { Checkbox } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import cx from 'classnames';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { batch } from 'react-redux';
import { DoorStoreSingleContext } from '../../../../../DoorModal/DoorStoreSingleContext';
import {
	setExitReaderAssuranceLevel,
	setExitReaderCCOTZAssurance,
	setExitReaderDefaultAssuranceLevel,
	setExitReaderError,
	setExitReaderErrorType,
} from '../../../exitReaderContext';
import styles from '../../Logic/logic.module.scss';

const AssuranceLevels: React.FC = () => {
	//#region state management variables
	const {
		contextStateDoor: {
			exitReader: {
				rS485ReaderTypes,
				defaultAssuranceLevels,
				assuranceLevels,
				cCOTZAssurance,
				readerErrorType,
				readerErrorMessage,
				selections: { rS485ReaderType, defaultAssuranceLevel, assuranceLevel },
				isExitReaderError,
			},
		},
		dispatcherDoor,
	} = useContext(DoorStoreSingleContext);

	const [assurance, setAssurance] = useState(undefined);
	const [readerName, setReaderName] = useState('');
	const [assuranceLevelsList, setAssuranceLevelsList] = useState<SelectOptions<string>[]>([]);
	const [defaultAssuranceLevelsList, setDefaultAssuranceLevelsList] = useState<SelectOptions<string>[]>([]);

	const errorMessage: JSX.Element = useMemo(() => {
		if (readerErrorType === ReaderError.Assurance || readerErrorType === ReaderError.DefaultAssuranceLevel) {
			return <span className={styles.errorMessage}>{readerErrorMessage}</span>;
		} else {
			return <></>;
		}
	}, [readerErrorType]);
	//#endregion

	useEffect(() => {
		const index: number = rS485ReaderTypes.findIndex(x => x.Id === rS485ReaderType);
		if (index > -1) {
			setReaderName(rS485ReaderTypes[index].Name);
			setAssurance(assuranceLevel !== 0 ? assuranceLevel.toString() : undefined);
		}
	}, [rS485ReaderType]);

	useEffect(() => {
		let defaultAssuranceLevelOpts: SelectOptions<string>[] = defaultAssuranceLevels.map(x => ({
			label: x.Name,
			value: x.Id.toString(),
			id: x.Id.toString(),
		}));
		if (defaultAssuranceLevel < 1) {
			defaultAssuranceLevelOpts = [{ label: ' ', value: '-1', id: '-1' }, ...defaultAssuranceLevelOpts];
		}

		let assuranceLevelOpts: SelectOptions<string>[] = assuranceLevels.map(x => ({
			label: x.Name,
			value: x.Id.toString(),
			id: x.Id.toString(),
		}));
		const isAssuranceLevelOnList = assuranceLevels.find(x => x.Id === assuranceLevel) !== undefined;
		if (!isAssuranceLevelOnList || assuranceLevel < 1) {
			if (!isAssuranceLevelOnList) {
				dispatcherDoor(setExitReaderAssuranceLevel(Number(0)));
				setAssurance('0');
			}
			assuranceLevelOpts = [{ label: ' ', value: '0', id: '0' }, ...assuranceLevelOpts];
		}
		setDefaultAssuranceLevelsList(defaultAssuranceLevelOpts);
		setAssuranceLevelsList(assuranceLevelOpts);
	}, [defaultAssuranceLevels, assuranceLevels]);

	const handleOnChangeAssurance = (value: string) => {
		batch(() => {
			setAssurance(value);
			dispatcherDoor(setExitReaderAssuranceLevel(Number(value)));

			if (readerErrorType === ReaderError.Assurance) {
				dispatcherDoor(setExitReaderErrorType(undefined));
				dispatcherDoor(setExitReaderError(false));
			}
		});
	};

	const handleSetCCOTZAssurance = (value: CheckboxChangeEvent) => {
		batch(() => {
			if (!value.target.checked) {
				setAssurance(undefined);
				dispatcherDoor(setExitReaderAssuranceLevel(0));
			}
			dispatcherDoor(setExitReaderCCOTZAssurance(value.target.checked));

			if (readerErrorType === ReaderError.Assurance) {
				dispatcherDoor(setExitReaderErrorType(undefined));
				dispatcherDoor(setExitReaderError(false));
			}
		});
	};

	const handleOnChangeDefaultAssurance = (value: string) => {
		batch(() => {
			dispatcherDoor(setExitReaderDefaultAssuranceLevel(Number(value)));
			if (readerErrorType === ReaderError.DefaultAssuranceLevel) {
				dispatcherDoor(setExitReaderErrorType(undefined));
				dispatcherDoor(setExitReaderError(false));
			}
		});
	};

	const isDefaultAssuranceLevelError = readerErrorType === ReaderError.DefaultAssuranceLevel && isExitReaderError;
	const isAssuranceLevelError = readerErrorType === ReaderError.Assurance && isExitReaderError;

	return (
		<>
			<div className={styles.options}>
				<div className={styles.sectionContainer}>
					<fieldset>
						<legend className={styles.legend}>{_('AssuranceLevel')}</legend>
						<div className={styles.readerType}>
							{readerName} {_('reader')}
						</div>
						<div className={styles.assuranceSelect}>
							<div>
								<Select
									className={cx(styles.select, {
										[styles.error]: isDefaultAssuranceLevelError,
									})}
									options={defaultAssuranceLevelsList}
									onChange={handleOnChangeDefaultAssurance}
									key='defaultAssurance'
									id='exitReadeModalDefaultAssuranceSelect'
									value={defaultAssuranceLevel.toString()}
									getPopupContainer={() => document.getElementById('logicExitReaderContainer')}
								/>
								{isDefaultAssuranceLevelError && errorMessage}
							</div>
							<span>{_('DefaultAssuranceLevel')}</span>
						</div>
						<div className={styles.ccotzAssuranceSection}>
							<div className={styles.ccotzAssurance}>
								<Checkbox checked={cCOTZAssurance} onChange={handleSetCCOTZAssurance} id={'ccotzAssurance'}>
									<span>{_('EnableLowerAssurance')}</span>
								</Checkbox>
							</div>
						</div>
						<div className={styles.assuranceSelect}>
							<div>
								<Select
									className={cx(styles.select, {
										[styles.error]: isAssuranceLevelError,
									})}
									options={assuranceLevelsList}
									onChange={handleOnChangeAssurance}
									key='assurance'
									value={assurance}
									id='exitReadeModalAssuranceLevelListSelect'
									disabled={!cCOTZAssurance}
									getPopupContainer={() => document.getElementById('logicExitReaderContainer')}
								/>
								{isAssuranceLevelError && errorMessage}
							</div>
							<span>{_('CCOTZAssurance')}</span>
						</div>
					</fieldset>
				</div>
			</div>
		</>
	);
};

export { AssuranceLevels };
